<template>
    <div>
        <div v-if="loading" class="preloader">
            <CSpinner color="primary" />
        </div>
        <CRow v-else-if="company">
            <CCol xl="6">
                <CCard>
                    <CCardHeader>
                        <strong>{{ company.title }}</strong>
                        <CBadge class="ml-2" :color="paymentOk ? 'success' : 'danger'">
                            {{ paymentOk ? 'Оплата в порядке' : (company.overdue ? 'Просрочена оплата' : 'Есть долг') }}
                        </CBadge>
                    </CCardHeader>
                    <CCardBody>
                        <CInput v-model="form.title" label="Название" :horizontal="true" />
                        <CInput v-model="form.legal_entity" label="Юр. лицо" :horizontal="true" />
                        <CInput v-model="form.inn" label="ИНН" :horizontal="true" />
                        <CSelect
                            :value.sync="form.program_type"
                            :horizontal="true"
                            :options="programOptions"
                            label="Тип программы"
                        />
                        <CSelect
                            :value.sync="form.tariff_preset"
                            :horizontal="true"
                            :options="tariffOptions"
                            label="Тариф"
                        />
                        <CInput
                            v-if="form.tariff_preset === 'custom'"
                            v-model.number="form.tariff_amount"
                            type="number"
                            label="Кастомная сумма, ₽"
                            :horizontal="true"
                        />
                        <CRow class="mb-3">
                            <CCol sm="3" class="col-form-label">Активна</CCol>
                            <CCol sm="9">
                                <CSwitch
                                    color="dark"
                                    shape="pill"
                                    variant="opposite"
                                    :checked.sync="form.enabled"
                                />
                            </CCol>
                        </CRow>
                        <div class="mb-2">Остаток: <strong>{{ formatMoney(company.remaining_balance) }}</strong></div>
                        <div class="mb-2">Внесено: <strong>{{ formatMoney(company.paid_total) }}</strong></div>
                        <div class="mb-2">Оплачено заказами: <strong>{{ formatMoney(company.spent_total) }}</strong></div>
                        <div class="mb-2">
                            Долг:
                            <strong :class="{ 'text-danger': Number(company.current_debt) > 0 }">
                                {{ formatMoney(company.current_debt) }}
                            </strong>
                        </div>
                        <div
                            v-if="company.program_type === 'prepaid' && !(Number(company.paid_total) > 0)"
                            class="text-muted"
                        >
                            Корпоративный счёт активируется после первого платежа.
                        </div>
                    </CCardBody>
                    <CCardFooter>
                        <CButton color="primary" :disabled="saving" @click="save">Сохранить</CButton>
                    </CCardFooter>
                </CCard>

                <CCard>
                    <CCardHeader><strong>Профили</strong></CCardHeader>
                    <CCardBody>
                        <CInput
                            v-model="newPhone"
                            label="Телефон профиля"
                            placeholder="+7..."
                            :horizontal="true"
                        />
                        <CButton color="info" size="sm" class="mb-3" :disabled="addingMember" @click="addMember">
                            Добавить профиль
                        </CButton>
                        <CDataTable hover :items="members" :fields="memberFields">
                            <template #phone="data">
                                <td>
                                    <CLink :to="`/customers/${data.item.customer.id}`">
                                        {{ data.item.customer.phone }}
                                    </CLink>
                                </td>
                            </template>
                            <template #name="data">
                                <td>{{ data.item.customer.firstname }} {{ data.item.customer.lastname || '' }}</td>
                            </template>
                            <template #actions="data">
                                <td>
                                    <CButton color="danger" size="sm" @click="removeMember(data.item.customer_id)">
                                        Убрать
                                    </CButton>
                                </td>
                            </template>
                        </CDataTable>
                    </CCardBody>
                </CCard>
            </CCol>
            <CCol xl="6">
                <CCard>
                    <CCardHeader><strong>Платежи компании</strong></CCardHeader>
                    <CCardBody>
                        <CInput
                            v-model.number="paymentForm.amount"
                            type="number"
                            label="Сумма, ₽"
                            :horizontal="true"
                        />
                        <CInput
                            v-model="paymentForm.paid_at"
                            type="date"
                            label="Дата платежа"
                            :horizontal="true"
                        />
                        <CInput
                            v-model="paymentForm.comment"
                            label="Комментарий"
                            :horizontal="true"
                        />
                        <CButton
                            color="success"
                            size="sm"
                            class="mb-3"
                            :disabled="addingPayment"
                            @click="addPayment"
                        >
                            Внести платёж
                        </CButton>
                        <CDataTable hover :items="payments" :fields="paymentFields">
                            <template #paid_at="data">
                                <td>{{ formatMskDateTime(data.item.paid_at) }}</td>
                            </template>
                            <template #amount="data">
                                <td><nobr>{{ formatMoney(data.item.amount) }}</nobr></td>
                            </template>
                            <template #comment="data">
                                <td>{{ data.item.comment || '' }}</td>
                            </template>
                            <template #admin="data">
                                <td>{{ data.item.admin && data.item.admin.name ? data.item.admin.name : '—' }}</td>
                            </template>
                            <template #actions="data">
                                <td>
                                    <CButton color="danger" size="sm" @click="removePayment(data.item.id)">
                                        Удалить
                                    </CButton>
                                </td>
                            </template>
                        </CDataTable>
                    </CCardBody>
                </CCard>

                <CCard>
                    <CCardHeader><strong>Расчётные периоды</strong></CCardHeader>
                    <CCardBody>
                        <CDataTable hover :items="periods" :fields="periodFields">
                            <template #period_start="data">
                                <td>{{ formatMskDate(data.item.period_start) }}</td>
                            </template>
                            <template #period_end="data">
                                <td>{{ formatMskDate(data.item.period_end) }}</td>
                            </template>
                            <template #spent_amount="data">
                                <td><nobr>{{ formatMoney(data.item.spent_amount) }}</nobr></td>
                            </template>
                            <template #debt_amount="data">
                                <td><nobr>{{ formatMoney(data.item.debt_amount) }}</nobr></td>
                            </template>
                            <template #status="data">
                                <td>
                                    <CBadge :color="periodStatusColor(data.item.status)">
                                        {{ periodStatusLabel(data.item.status) }}
                                    </CBadge>
                                </td>
                            </template>
                            <template #actions="data">
                                <td>
                                    <CButton
                                        v-if="data.item.status === 'unpaid' || data.item.status === 'overdue'"
                                        color="success"
                                        size="sm"
                                        @click="markPaid(data.item.id)"
                                    >
                                        Счёт оплачен
                                    </CButton>
                                </td>
                            </template>
                        </CDataTable>
                    </CCardBody>
                </CCard>

                <CCard>
                    <CCardHeader><strong>Заказы компании</strong></CCardHeader>
                    <CCardBody>
                        <CDataTable hover :items="orders" :fields="orderFields">
                            <template #id="data">
                                <td>
                                    <CLink :to="`/orders/${data.item.id}`">#{{ data.item.id }}</CLink>
                                </td>
                            </template>
                            <template #payment_method="data">
                                <td>{{ data.item.payment_method === 'company_account' ? 'Корпоративный счёт' : 'Другое' }}</td>
                            </template>
                            <template #price="data">
                                <td>{{ formatMoney(orderTotal(data.item)) }}</td>
                            </template>
                            <template #created_at="data">
                                <td>{{ formatMskDateTime(data.item.created_at) }}</td>
                            </template>
                        </CDataTable>
                    </CCardBody>
                </CCard>
            </CCol>
        </CRow>
    </div>
</template>

<script>
    import Vue from 'vue';

    export default {
        name: 'BusinessCompany',
        data() {
            return {
                loading: true,
                saving: false,
                addingMember: false,
                addingPayment: false,
                company: null,
                newPhone: '',
                paymentForm: {
                    amount: null,
                    paid_at: '',
                    comment: ''
                },
                form: {
                    title: '',
                    legal_entity: '',
                    inn: '',
                    program_type: 'prepaid',
                    tariff_preset: '20000',
                    tariff_amount: 20000,
                    enabled: true
                },
                programOptions: [
                    { value: 'prepaid', label: 'Предоплата' },
                    { value: 'installment', label: 'Рассрочка' }
                ],
                tariffOptions: [
                    { value: '20000', label: '20 000 ₽' },
                    { value: '50000', label: '50 000 ₽' },
                    { value: '100000', label: '100 000 ₽' },
                    { value: 'custom', label: 'Кастомная сумма' }
                ],
                memberFields: [
                    { key: 'phone', label: 'Телефон' },
                    { key: 'name', label: 'Имя' },
                    { key: 'actions', label: '' }
                ],
                periodFields: [
                    { key: 'period_start', label: 'Начало' },
                    { key: 'period_end', label: 'Конец' },
                    { key: 'spent_amount', label: 'Потрачено' },
                    { key: 'debt_amount', label: 'Долг' },
                    { key: 'status', label: 'Статус' },
                    { key: 'actions', label: '' }
                ],
                paymentFields: [
                    { key: 'paid_at', label: 'Дата' },
                    { key: 'amount', label: 'Сумма' },
                    { key: 'comment', label: 'Комментарий' },
                    { key: 'admin', label: 'Кто внёс' },
                    { key: 'actions', label: '' }
                ],
                orderFields: [
                    { key: 'id', label: 'Заказ' },
                    { key: 'payment_method', label: 'Оплата' },
                    { key: 'price', label: 'Сумма' },
                    { key: 'created_at', label: 'Дата' }
                ]
            };
        },
        computed: {
            members() {
                return this.company && this.company.members ? this.company.members : [];
            },
            periods() {
                return this.company && this.company.periods ? this.company.periods : [];
            },
            orders() {
                return this.company && this.company.orders ? this.company.orders : [];
            },
            payments() {
                return this.company && this.company.payments ? this.company.payments : [];
            },
            paymentOk() {
                if (!this.company) {
                    return true;
                }
                if (this.company.overdue) {
                    return false;
                }
                return !(Number(this.company.current_debt) > 0);
            }
        },
        async mounted() {
            await this.load();
        },
        methods: {
            async load() {
                this.loading = true;
                try {
                    const { data } = await Vue.axios.get(`/companies/${this.$route.params.id}`);
                    this.company = data;
                    this.form = {
                        title: data.title,
                        legal_entity: data.legal_entity || '',
                        inn: data.inn || '',
                        program_type: data.program_type,
                        tariff_preset: data.tariff_preset,
                        tariff_amount: data.tariff_amount,
                        enabled: !!data.enabled
                    };
                    if (!this.paymentForm.paid_at) {
                        this.paymentForm.paid_at = this.todayDate();
                    }
                } catch (err) {
                    this.$root.$emit('showError', 'Не удалось загрузить компанию.');
                } finally {
                    this.loading = false;
                }
            },
            async save() {
                this.saving = true;
                try {
                    const { data } = await Vue.axios.put(`/companies/${this.company.id}`, this.form);
                    this.company = { ...this.company, ...data };
                    this.$root.$emit('showSuccess', 'Компания сохранена.');
                } catch (err) {
                    this.$root.$emit('showError', err.response?.data?.error || 'Не удалось сохранить.');
                } finally {
                    this.saving = false;
                }
            },
            async addMember() {
                this.addingMember = true;
                try {
                    await Vue.axios.post(`/companies/${this.company.id}/members`, { phone: this.newPhone });
                    this.newPhone = '';
                    await this.load();
                } catch (err) {
                    this.$root.$emit('showError', err.response?.data?.error || 'Не удалось добавить профиль.');
                } finally {
                    this.addingMember = false;
                }
            },
            async removeMember(customerId) {
                try {
                    await Vue.axios.delete(`/companies/${this.company.id}/members/${customerId}`);
                    await this.load();
                } catch (err) {
                    this.$root.$emit('showError', err.response?.data?.error || 'Не удалось убрать профиль.');
                }
            },
            async markPaid(periodId) {
                try {
                    await Vue.axios.post(`/companies/periods/${periodId}/mark-paid`);
                    await this.load();
                } catch (err) {
                    this.$root.$emit('showError', err.response?.data?.error || 'Не удалось отметить оплату.');
                }
            },
            async addPayment() {
                if (!(Number(this.paymentForm.amount) > 0)) {
                    this.$root.$emit('showError', 'Укажите сумму платежа.');
                    return;
                }
                this.addingPayment = true;
                try {
                    await Vue.axios.post(`/companies/${this.company.id}/payments`, this.paymentForm);
                    this.paymentForm = {
                        amount: null,
                        paid_at: this.todayDate(),
                        comment: ''
                    };
                    await this.load();
                    this.$root.$emit('showSuccess', 'Платёж внесён.');
                } catch (err) {
                    this.$root.$emit('showError', err.response?.data?.error || 'Не удалось внести платёж.');
                } finally {
                    this.addingPayment = false;
                }
            },
            async removePayment(paymentId) {
                if (!window.confirm('Удалить этот платёж?')) {
                    return;
                }
                try {
                    await Vue.axios.delete(`/companies/${this.company.id}/payments/${paymentId}`);
                    await this.load();
                } catch (err) {
                    this.$root.$emit('showError', err.response?.data?.error || 'Не удалось удалить платёж.');
                }
            },
            todayDate() {
                const now = new Date();
                const pad = value => String(value).padStart(2, '0');
                return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
            },
            orderTotal(order) {
                if (!order) {
                    return 0;
                }
                return (+order.price || 0) +
                    (+order.delivery_price || 0) +
                    (+order.waiting_price || 0) +
                    (+order.service_fee || 0);
            },
            formatMoney(value) {
                return `${Number(value || 0).toLocaleString('ru-RU')} ₽`;
            },
            formatMskDate(value) {
                return this.formatMsk(value, false);
            },
            formatMskDateTime(value) {
                return this.formatMsk(value, true);
            },
            formatMsk(value, withTime) {
                if (!value) {
                    return '—';
                }
                const raw = String(value).trim();
                let date;
                if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                    date = new Date(`${raw}T00:00:00+03:00`);
                } else if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
                    date = new Date(`${raw.replace(' ', 'T')}+03:00`);
                } else {
                    date = new Date(value);
                }
                if (Number.isNaN(date.getTime())) {
                    return raw;
                }
                return new Intl.DateTimeFormat('ru-RU', {
                    timeZone: 'Europe/Moscow',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    ...(withTime
                        ? { hour: '2-digit', minute: '2-digit' }
                        : {})
                }).format(date);
            },
            periodStatusLabel(status) {
                return {
                    open: 'Открыт',
                    unpaid: 'Не оплачен',
                    paid: 'Оплачен',
                    overdue: 'Просрочен'
                }[status] || status;
            },
            periodStatusColor(status) {
                return {
                    open: 'info',
                    unpaid: 'warning',
                    paid: 'success',
                    overdue: 'danger'
                }[status] || 'secondary';
            }
        }
    };
</script>
